/**
 * 라우트 (DEV_SPEC 3장).
 *
 * - 하단 탭은 상회칠판 · 별채대화 · 나 에만 붙습니다 (TabLayout, 시안 03 README 5.13). 첫 화면은 별채대화(/)
 * - 멤버 소개(/members)는 탭이 아니라 하위 화면입니다(N4)
 * - 근거 시트는 상태가 아니라 경로입니다. 안드로이드 뒤로 가기가 시트만 닫아야 합니다 (히어로챗 B-4).
 *   대화에는 근거 칩을 붙이지 않아(D18), 시트는 매일 콘텐츠 카드에서만 엽니다(별채대화 /evidence, 상회칠판 /board/evidence)
 * - /dev/components 는 개발 빌드에서만 있는 공통 컴포넌트 보기입니다
 */
import { LegalIndexPage, LegalPage } from './pages/legal/LegalPage.jsx';
import { ConsentPage } from './pages/consent/ConsentPage.jsx';
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TabLayout } from './components/TabLayout.jsx';
import { GuestOnly, RequireMember, RequireOnboarding, SessionGate } from './components/RouteGuards.jsx';
import { LoginPage } from './pages/login/LoginPage.jsx';
import { StartPage } from './pages/login/StartPage.jsx';
import { WelcomePage } from './pages/welcome/WelcomePage.jsx';
import { CounterPage } from './pages/counter/CounterPage.jsx';
import { BoardPage } from './pages/board/BoardPage.jsx';
import { RoomPage } from './pages/room/RoomPage.jsx';
import { WorryPage } from './pages/worry/WorryPage.jsx';
import { MemberPostPage } from './pages/board/MemberPostPage.jsx';
import { ChatPage } from './pages/chat/ChatPage.jsx';
import { MembersPage } from './pages/members/MembersPage.jsx';
import { MemberProfilePage } from './pages/members/MemberProfilePage.jsx';
import { MePage } from './pages/me/MePage.jsx';
import { MemoBookPage } from './pages/me/MemoBookPage.jsx';
import { DeleteAccountPage } from './pages/me/DeleteAccountPage.jsx';
import { GoodbyePage } from './pages/me/GoodbyePage.jsx';
import { MemoToast } from './components/memo/Memo.jsx';
import { NoteSheetHost } from './components/memo/NoteSheet.jsx';
import { useAppActivity } from './hooks/useAppActivity.js';
import { useNativePush } from './hooks/useNativePush.js';

const ComponentsPage = import.meta.env.DEV
    ? lazy(() => import('./pages/dev/ComponentsPage.jsx').then((module) => ({ default: module.ComponentsPage })))
    : null;

export function App() {
    // 앱 열기 · 머문 시간 (명세 v1.3 10장, D44)
    useAppActivity();
    // 앱(안드로이드) 푸시: 기기 등록 · 알림 누르면 그 화면(DEV-12)
    useNativePush();
    return (
        <div className="mx-auto min-h-dvh max-w-app bg-bg">
            <SessionGate>
                <Routes>
                    {/* 로그인 · 가입 (D8: 이메일/암호) */}
                    <Route element={<GuestOnly />}>
                        <Route path="/start" element={<StartPage />} />
                        <Route path="/login" element={<LoginPage />} />
                    </Route>
                    {/* S0a 골목 · S0b 카운터 (DEV_SPEC 4.1). 첫 방문을 마치지 않은 사람만, 멈춘 장면으로 */}
                    {/* 약관 보기: 로그인 없이(DEV-07r · D97) */}
                    <Route path="/legal" element={<LegalIndexPage />} />
                    <Route path="/legal/:doc" element={<LegalPage />} />
                    {/* 가입 동의: 지금 판에 동의하지 않은 단골은 첫 방문 · 별채보다 먼저 */}
                    <Route element={<RequireOnboarding path="/consent" />}>
                        <Route path="/consent" element={<ConsentPage />} />
                    </Route>
                    <Route element={<RequireOnboarding path="/welcome" />}>
                        <Route path="/welcome" element={<WelcomePage />} />
                    </Route>
                    <Route element={<RequireOnboarding path="/counter" />}>
                        <Route path="/counter" element={<CounterPage />} />
                    </Route>
                    <Route element={<RequireMember />}>
                        <Route element={<TabLayout />}>
                            {/* S1 별채대화(홈) */}
                            <Route path="/" element={<RoomPage />} />
                            <Route path="/evidence/:evidenceKey" element={<RoomPage />} />
                            {/* S1b 상회칠판 */}
                            <Route path="/board" element={<BoardPage />} />
                            <Route path="/board/evidence/:evidenceKey" element={<BoardPage />} />
                            {/* M1 나 */}
                            <Route path="/me" element={<MePage />} />
                            <Route path="/me/memo" element={<MemoBookPage />} />
                        </Route>
                        {/* N4 멤버 소개 */}
                        <Route path="/members" element={<MembersPage />} />
                        {/* S2 고민 스레드 */}
                        <Route path="/worry/:worryId" element={<WorryPage />} />
                        <Route path="/board/posts/:worryId" element={<WorryPage />} />
                        <Route path="/board/member-posts/:memberPostId" element={<MemberPostPage />} />
                        {/* S3 1:1 대화 */}
                        <Route path="/chat/:memberKey" element={<ChatPage />} />
                        {/* S4b 멤버 프로필 */}
                        <Route path="/members/:memberKey" element={<MemberProfilePage />} />
                        {/* M3 회원 탈퇴 (시안 06) */}
                        <Route path="/me/delete" element={<DeleteAccountPage />} />
                    </Route>
                    {/* M4 탈퇴 완료: 세션을 지운 뒤라 지킴 없이 */}
                    <Route path="/goodbye" element={<GoodbyePage />} />
                    {ComponentsPage ? (
                        <Route
                            path="/dev/components"
                            element={
                                <Suspense fallback={null}>
                                    <ComponentsPage />
                                </Suspense>
                            }
                        />
                    ) : null}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <MemoToast />
                <NoteSheetHost />
            </SessionGate>
        </div>
    );
}
